import { authenticatedFetch, extractApiErrorMessage, resolvePeriverseApiBaseUrl } from '../shared';
import {
  CountSheetResponseDtoSchema,
  PaginatedCountSheetResponseDtoSchema,
  type CountSheetListQueryParams,
  type CountSheetResponseDto,
  type CreateCountSheetDto,
  type PaginatedCountSheetResponseDto,
  type ReplaceCountSheetDto,
} from '@peritasai/types';

type BaseUrlResolver = () => string;

export class CountSheetsApiClient {
  constructor(
    private readonly resolveBaseUrl: BaseUrlResolver = resolvePeriverseApiBaseUrl
  ) {}

  async getCountSheets(): Promise<CountSheetResponseDto[]> {
    const url = `${this.resolveBaseUrl()}/count-sheets`;
    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: { accept: 'application/json' },
    });
    if (!response.ok) throw new Error(await extractApiErrorMessage(response));
    const parsed = PaginatedCountSheetResponseDtoSchema.parse(await response.json());
    return parsed.data;
  }

  async createCountSheet(dto: CreateCountSheetDto): Promise<CountSheetResponseDto> {
    const url = `${this.resolveBaseUrl()}/count-sheets`;
    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify(dto),
    });
    if (!response.ok) throw new Error(await extractApiErrorMessage(response));
    return CountSheetResponseDtoSchema.parse(await response.json());
  }

  async replaceCountSheet(id: string, dto: ReplaceCountSheetDto): Promise<CountSheetResponseDto> {
    const url = `${this.resolveBaseUrl()}/count-sheets/${id}`;
    const response = await authenticatedFetch(url, {
      method: 'PUT',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify(dto),
    });
    if (!response.ok) throw new Error(await extractApiErrorMessage(response));
    return CountSheetResponseDtoSchema.parse(await response.json());
  }

  async getCountSheetById(id: string): Promise<CountSheetResponseDto> {
    const url = `${this.resolveBaseUrl()}/count-sheets/${id}`;
    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: { accept: 'application/json' },
    });
    if (!response.ok) throw new Error(await extractApiErrorMessage(response));
    return CountSheetResponseDtoSchema.parse(await response.json());
  }

  async getPagedCountSheets(params: CountSheetListQueryParams): Promise<PaginatedCountSheetResponseDto> {
    const { page, limit, search } = params;
    if (page < 1) throw new Error(`getPagedCountSheets: page must be >= 1, received ${page}`);
    const query = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) query.set('search', search);
    const url = `${this.resolveBaseUrl()}/count-sheets?${query.toString()}`;
    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: { accept: 'application/json' },
    });
    if (!response.ok) throw new Error(await extractApiErrorMessage(response));
    return PaginatedCountSheetResponseDtoSchema.parse(await response.json());
  }

  async searchCountSheets(trayType: string): Promise<CountSheetResponseDto | null> {
    const url = `${this.resolveBaseUrl()}/count-sheets/search?trayType=${encodeURIComponent(trayType)}`;
    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: { accept: 'application/json' },
    });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error(await extractApiErrorMessage(response));
    return CountSheetResponseDtoSchema.parse(await response.json());
  }
}

export const createCountSheetsApiClient = (
  resolveBaseUrl: BaseUrlResolver = resolvePeriverseApiBaseUrl
): CountSheetsApiClient => new CountSheetsApiClient(resolveBaseUrl);

export const countSheetsApiClient = new CountSheetsApiClient();
